import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useOrdering } from '../ordering/OrderingContext';
import { fmt$ } from '../ordering/orderApi';

/**
 * Bottom-sheet style modal for item details.
 * Slides up on mobile, centered card on desktop.
 * Closes on overlay tap, Escape key, or swipe-down.
 */
const ItemModal = ({ item, onClose }) => {
  const modalRef = useRef(null);
  const startY = useRef(0);
  const { canOrder, addToCart } = useOrdering();
  const [qty, setQty] = useState(1);
  const canBuy = canOrder && item && Number(item.price) > 0;

  // Close on Escape key + lock background scroll
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);

    // Robust scroll-lock: position:fixed preserves & restores scroll on iOS
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, [onClose]);

  // Auto-focus the modal for accessibility
  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  // Swipe-down to dismiss on mobile
  const handleTouchStart = useCallback((e) => {
    startY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e) => {
      const deltaY = e.changedTouches[0].clientY - startY.current;
      if (deltaY > 100) onClose(); // swipe down > 100px → dismiss
    },
    [onClose]
  );

  if (!item) return null;

  return (
    <div className="fm-modal-overlay" onClick={onClose}>
      <div
        className="fm-modal"
        ref={modalRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fm-modal-title"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag handle for mobile */}
        <div className="fm-modal__handle" aria-hidden="true" />

        <button
          className="fm-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="fm-modal__content">
          <h3 className="fm-modal__title" id="fm-modal-title">
            {item.name}
          </h3>

          {item.img && (
            <div className="fm-modal__media">
              <img
                src={item.img}
                alt={item.name}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.parentElement.style.display = 'none';
                }}
              />
            </div>
          )}

          <p className="fm-modal__desc">
            {item.desc || 'No description available.'}
          </p>

          {canBuy && (
            <div className="fm-modal__order">
              <div className="fm-modal__order-row">
                <span className="fm-modal__price">{fmt$(item.price)}</span>
                <div className="ord-qty ord-qty--lg">
                  <button onClick={() => setQty(q => Math.max(1, q - 1))} aria-label="Decrease">−</button>
                  <span>{qty}</span>
                  <button onClick={() => setQty(q => Math.min(99, q + 1))} aria-label="Increase">+</button>
                </div>
              </div>
              <button
                className="ord-btn ord-btn--primary fm-modal__add"
                onClick={() => { addToCart(item, qty); onClose(); }}>
                Add {qty} to cart · {fmt$(item.price * qty)}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ItemModal);
